import { Permissions } from './../../../../core/constants/permissions';
import { AuthService } from './../../../../core/services/auth.service';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  ExpenseClaimPaymentMethod,
  ExpenseClaimState,
  ExpenseClaimViewModel,
  ExpenseCurrencyGroup,
  isNmiblePaymentMethod,
  NmibleManualPaymentStatus
} from '../../../../core/models/expense.model';
import { RoleNames } from "../../../../core/constants/role-names";
import { SplitButtonAction } from "../../../../shared/split-button/split-button.component";
import { environment } from "../../../../../environments/environment";

@Component({
  selector: 'app-expense-caxton-payment-actions',
  templateUrl: './expense-caxton-payment-actions.component.html',
  styleUrls: ['./expense-caxton-payment-actions.component.scss']
})
export class ExpenseCaxtonPaymentActionsComponent implements OnInit, OnChanges {
  @Input() expenses: ExpenseCurrencyGroup;
  @Input() selectedTab: ExpenseClaimState;
  @Input() processingRequest: boolean;
  @Input() dataLoaded: boolean;

  @Output() automatedPaymentFailed: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() sendToFinance: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() payByBank: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() payByCard: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() payByNmible: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() payByNmibleBank: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() checkNmibleStatus: EventEmitter<ExpenseCurrencyGroup> = new EventEmitter<ExpenseCurrencyGroup>();
  @Output() unloadFromCard: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() investigation: EventEmitter<ExpenseClaimViewModel[]> = new EventEmitter<ExpenseClaimViewModel[]>();
  @Output() transfer: EventEmitter<ExpenseCurrencyGroup> = new EventEmitter<ExpenseCurrencyGroup>();
  @Output() updateNmiblePaymentStatusManually: EventEmitter<{ expenses: ExpenseCurrencyGroup, status: NmibleManualPaymentStatus }> =
    new EventEmitter<{ expenses: ExpenseCurrencyGroup, status: NmibleManualPaymentStatus }>();

  readonly ExpenseClaimState = ExpenseClaimState;

  readonly nmiblePaymentStatusActions: SplitButtonAction[] = [
    { label: 'Update to Success', value: NmibleManualPaymentStatus.Success.toString(), default: true, variant: 'secondary' },
    { label: 'Update to In Progress', value: NmibleManualPaymentStatus.InProgress.toString() },
    { label: 'Update to Queued', value: NmibleManualPaymentStatus.Queued.toString() },
    { label: 'Update to Failure', value: NmibleManualPaymentStatus.Failure.toString(), dropdownVariant: 'danger' },
  ];

  canShowPatientName = false;
  hasWritePermission = false;

  showInvestigationBtn = false;
  showManualPaymentBtn = false;
  showPayByCaxtonBankBtn = false;
  showPayByCaxtonCardBtn = false;
  showUnloadCaxtonBtn = false;
  showPayByNmibleBtn = false;
  showPayByNmibleBankBtn = false;
  showCheckNmibleStatusBtn = false;
  showRetryNmibleBtn = false;
  showRetryCaxtonBtn = false;
  showFoundOnCaxtonBtn = false;
  showUpdateNmiblePaymentStatusManuallyBtn = false;

  constructor(public authService: AuthService) { }

  ngOnInit(): void {
    this.hasWritePermission = this.authService.hasPermission(Permissions.ExpenseWrite);
    this.updateVisibilityFlags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expenses'] || changes['selectedTab']) {
      this.updateVisibilityFlags();
    }
  }

  private updateVisibilityFlags(): void {
    if (!this.expenses) return;

    this.canShowPatientName = !this.authService.hasRole(RoleNames.BlindedProjectCoordinatorExpenses);

    const canWriteInTab = (tabState: ExpenseClaimState) =>
      this.selectedTab === tabState && this.hasWritePermission;

    this.showInvestigationBtn = canWriteInTab(ExpenseClaimState.Processing);
    this.showCheckNmibleStatusBtn =
      isNmiblePaymentMethod(this.expenses.paymentMethod) &&
      canWriteInTab(ExpenseClaimState.Processing);

    this.showUpdateNmiblePaymentStatusManuallyBtn =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.NmibleAutomatedBankTransfer &&
      canWriteInTab(ExpenseClaimState.Processing) &&
      (environment.environment === 'DEVELOPMENT' || environment.environment === 'STAGING');

    this.showManualPaymentBtn = canWriteInTab(ExpenseClaimState.AutomatedPaymentApproved);

    this.showPayByCaxtonBankBtn =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.CaxtonAutomatedBankTransfer &&
      canWriteInTab(ExpenseClaimState.AutomatedPaymentApproved);

    this.showPayByCaxtonCardBtn =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.CaxtonCard &&
      !this.expenses.isPreloadRedemptionGroup &&
      canWriteInTab(ExpenseClaimState.AutomatedPaymentApproved);

    this.showUnloadCaxtonBtn =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.CaxtonCard &&
      this.expenses.isPreloadRedemptionGroup &&
      canWriteInTab(ExpenseClaimState.AutomatedPaymentApproved);

    this.showPayByNmibleBtn =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.NmibleCard &&
      canWriteInTab(ExpenseClaimState.AutomatedPaymentApproved);

    this.showPayByNmibleBankBtn =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.NmibleAutomatedBankTransfer &&
      canWriteInTab(ExpenseClaimState.AutomatedPaymentApproved);

    const canWriteFailed = canWriteInTab(ExpenseClaimState.AutomatedPaymentFailed);

    const isCaxtonMethod =
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.CaxtonCard ||
      this.expenses.paymentMethod === ExpenseClaimPaymentMethod.CaxtonAutomatedBankTransfer;

    this.showRetryNmibleBtn =
      isNmiblePaymentMethod(this.expenses.paymentMethod) &&
      canWriteFailed;

    this.showRetryCaxtonBtn = isCaxtonMethod && canWriteFailed;

    this.showFoundOnCaxtonBtn = isCaxtonMethod && canWriteFailed;
  }

  onUpdateNmiblePaymentStatusManuallySelected(value: string): void {
    this.updateNmiblePaymentStatusManually.emit({
      expenses: this.expenses,
      status: Number(value) as NmibleManualPaymentStatus
    });
  }

}
